import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce } from "@uidotdev/usehooks";

import {
  selectActiveType,
  selectCurrentProduct,
  setCurrentProduct,
} from "./productSlice";
import {
  useDeleteProductMutation,
  useGetProductsPaginatedQuery,
  usePostProductMutation,
  useSearchProductsQuery,
  useGetEventsQuery,
} from "./productAPI";

import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import Divider from "@mui/material/Divider";

import { HOCList as List } from "../../common/components/List";
import { ActionsGroup } from "../../common/components/ActionsGroup";

import Dashboard from "./components/dashboard";
import Searchbar from "./components/searchbar";
import Pagination from "./components/pagination";

import * as typeBased from "./components/typeBased";
import { Placeholder } from "../../common/components/Placeholder";
import { isEmpty } from "lodash";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { CustomScrollbar } from "./components/customScrollbar/CustomScrollbar";
import {
  transformActiveType,
  transformActiveTypeText,
} from "./components/transformType/transformType.js";

export const Product = ({ type }) => {
  const dispatch = useDispatch();
  const activeType = useSelector(selectActiveType);
  const currentProduct = useSelector(selectCurrentProduct);
  const isDashboard = activeType === "dashboard";
  const isProductSelected = !isEmpty(currentProduct);

  const [dataResult, setDataResult] = useState("");
  const [page, setPage] = useState(0);
  const [mode, setMode] = useState("");
  const [isloading, setIsLoading] = useState(false);
  const [refreshData, setRefreshData] = useState(false);

  let queryHook;

  useEffect(() => {
    setPage(0); // Resetta la pagina solo quando cambia l'activeType
  }, [activeType]);

  if (activeType === "events") {
    queryHook = useGetEventsQuery;
  } else {
    queryHook = useGetProductsPaginatedQuery;
  }

  const {
    data: pageResult,
    isLoading: pageLoading,
    isSuccess: pageSuccess,
    refetch: refetchPageResult,
  } = queryHook({ activeType, page }, { skip: isDashboard });

  useEffect(() => {
    // console.log("dataresult: ", dataResult);
    // console.log("pageResult: ", pageResult);
    if (dataResult == null) setDataResult(pageResult);

    if (
      dataResult &&
      page >= dataResult.totalPages &&
      dataResult.totalPages > 0
    ) {
      setPage(dataResult.totalPages - 1);
    }
  }, [pageResult, dataResult, page]);

  const handlePage = (event, value) => {
    console.log("pageValue: ", value);
    if (value > 0) {
      setPage(value - 1);
    } else {
      setPage(0);
    }
  };

  const handleRefresh = () => {
    setRefreshData(true);
  };

  useEffect(() => {
    if (refreshData) {
      setIsLoading(true);
      refetchPageResult()
        .then(() => {
          setIsLoading(false);
          setRefreshData(false);
        })
        .catch((error) => {
          // console.error("Error refreshing data:", error);
          setIsLoading(false);
        });
    }
  }, [refreshData, refetchPageResult]);

  // Handle Searchbar
  const initialState = {
    products: {},
    missions: {},
    vouchersqr: {},
    vouchersint: {},
    vouchersext: {},
    promoPrefixes: {},
  };

  const [filters, setFilters] = useState(initialState);

  let searchQuery;

  const [query, setQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const toSkip =
    isDashboard ||
    (searchQuery === "" && debouncedSearchQuery === "") ||
    searchQuery !== debouncedSearchQuery;

  const handleFilterChange = (newFilters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [activeType]: {
        ...prevFilters[activeType],
        ...newFilters,
      },
    }));
  };

  useEffect(() => {
    const buildQuery = () => {
      let queryString = `${activeType}/`;

      if (searchQuery) {
        queryString += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const activeFilters = filters[activeType] || {};

      Object.keys(activeFilters).forEach((key) => {
        const value = activeFilters[key];
        if (
          value !== "" &&
          value !== false &&
          value !== undefined &&
          value !== null
        ) {
          queryString += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      return queryString;
    };

    setQuery(buildQuery());
  }, [searchQuery, filters, activeType, query, page]);

  function isObjectEmpty(obj) {
    return obj != null
      ? Object.keys(obj).length === 0 && obj.constructor === Object
      : false;
  }

  const {
    data: newDataResult,
    isLoading,
    isSuccess,
  } = useSearchProductsQuery(
    {
      type: activeType,
      searchQuery: searchQuery,
      filters: filters[activeType],
      page: page,
    },

    { skip: isDashboard || isObjectEmpty(filters[activeType]) }
  );

  // Update page state if it exceeds the available pages
  useEffect(() => {
    if (
      newDataResult != null &&
      pageResult !== newDataResult &&
      query !== activeType + "/"
    ) {
      setDataResult(newDataResult);
    } else {
      setDataResult(pageResult);
    }
    if (
      newDataResult &&
      page >= newDataResult.totalPages &&
      newDataResult.totalPages > 0
    ) {
      setPage(newDataResult.totalPages - 1);
    }
  }, [newDataResult, dataResult, query, activeType, page, pageResult]);

  // End Handle SearchBar

  const [postProduct, { isLoading: isSaving }] = usePostProductMutation();
  const handleSaveClick = (data) => {
    if (mode === "edit") {
      postProduct({ product: { ...currentProduct, ...data }, activeType });
    }
    if (mode === "create") {
      postProduct({ product: data, activeType });
    }
  };
  const [deleteProduct, {}] = useDeleteProductMutation();
  const handleDeleteClick = (product) => {
    deleteProduct({ activeType, id: product.id });
  };

  // List action handlers
  const handleEditClick = (product) => {
    setMode("edit");
    dispatch(setCurrentProduct(product));
  };

  useEffect(() => {
    setMode("");
    setPage(0);
    setDataResult(0);
  }, [activeType]);

  // Action group handlers
  const handleNewClick = () => {
    setMode("create");
    dispatch(setCurrentProduct({}));
  };
  const handleCancelClick = () => {
    setMode("");
    dispatch(setCurrentProduct({}));
  };

  // Dashboard renderer
  if (isDashboard) {
    return <Dashboard />;
  }

  const listItems = debouncedSearchQuery
    ? searchQuery
    : dataResult?.content || [];
  const { Form, Title, ListItemText } = typeBased[activeType];

  //RESPONSIVE LIST STYLE

  const getResponsiveStyles = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    let maxHeight, minHeight, paddingBottom;

    if (width <= 600) {
      // Per schermi piccoli
      maxHeight = "80vh";
      minHeight = "30vh";
      paddingBottom = "2vh";
    } else if (width >= 1200) {
      // Per schermi grandi
      maxHeight = "50vh";
      minHeight = "25vh";
      paddingBottom = "2vh";
    } else {
      // Per schermi medi
      maxHeight = "60vh";
      minHeight = "20vh";
      paddingBottom = "2vh";
    }

    return {
      maxHeight,
      minHeight,
      overflow: "auto",
      paddingBottom,
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <CustomScrollbar>
      <Box bgcolor="#f8f8f8" padding="1.5em" height={"100vh"}>
        <Grid2 container columnSpacing={2} rowSpacing={1} m={0}>
          <Grid2 xs={12}>
            <Typography variant="h3" color="primary" marginBottom={"2vh"}>
              <Title />
            </Typography>
          </Grid2>
          <Grid2 xs={1} display="flex" alignItems="center" width="fit-content">
            <Pagination
              show={dataResult && !searchQuery}
              handlePage={handlePage}
              count={dataResult?.totalPages}
            />
          </Grid2>

          <Grid2 xs={5} display="flex" flexDirection="column">
            <Searchbar onFilterChange={handleFilterChange} type={activeType} />
            <Button
              onClick={handleRefresh}
              disabled={isLoading}
              sx={{ display: "flex", marginTop: "1em" }}
            >
              {isLoading ? (
                <>
                  <Box sx={{ display: "flex", marginTop: "1em" }}>
                    <CircularProgress />
                  </Box>
                </>
              ) : (
                <>
                  <RefreshIcon sx={{ display: "flex", marginRight: "5px" }} />
                  <Typography variant={"subtitle2"}> Refresh List </Typography>
                </>
              )}
            </Button>

            {listItems.length > 0 ? (
              <List
                data={listItems}
                style={{
                  maxHeight: "28vw",
                  minHeight: "10vw",
                  overflow: "auto",
                }}
                isLoading={pageLoading}
                disableHeader={true}
                itemType={"product." + transformActiveType(activeType)}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
                itemText={ListItemText}
                generalDisable={!isProductSelected && mode !== "create"}
              />
            ) : (
              <Typography variant="h4" color="lightgrey" mt="20px">
                No {transformActiveTypeText(activeType)} found
              </Typography>
            )}
          </Grid2>
          <Grid2
            xs={1}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Divider orientation="vertical" flexItem>
              <ActionsGroup
                direction="vertical"
                loading={false}
                generalDisable={!isProductSelected && mode !== "create"}
                onNewClick={handleNewClick}
                onCancelClick={handleCancelClick}
              />
            </Divider>
          </Grid2>
          <Grid2 xs={5} display="flex" alignItems="center">
            {isProductSelected && mode === "edit" && (
              <Form
                mode={mode}
                handleSave={handleSaveClick}
                currentProduct={currentProduct}
              />
            )}
            {!isProductSelected && mode === "create" && (
              <Form
                mode={mode}
                handleSave={handleSaveClick}
                currentProduct={{}}
              />
            )}
            {!isProductSelected && mode !== "create" && (
              <Placeholder placeholderText="Select a product from the list" />
            )}
          </Grid2>
        </Grid2>
      </Box>
    </CustomScrollbar>
  );
};